import { BridgeComponent } from "@hotwired/hotwire-native-bridge";
import { playbackObservabilityValues, playbackObservabilityPayload } from "utils/playback_observability";

export default class LivePlayerController extends BridgeComponent {
  static component = "live-player";

  // Read via Stimulus values, not getAttribute: maybeConnectBridge renames every
  // data-media--video-player-* attribute onto this controller's identifier before connect() runs.
  static values = {
    ...playbackObservabilityValues
  };

  connect() {
    // Installs the library's native:restore listener, which re-runs connect() (re-sending
    // this payload) when the native shell restores the screen without a Turbo visit.
    super.connect();

    this.send("connect", {
      itemId: this.element.getAttribute("data-item-id"),
      itemType: this.element.getAttribute("data-item-type"),
      title: this.element.getAttribute("data-title"),
      streamType: "live",
      hlsUrl: this.element.getAttribute("data-hls-url"),
      // The identity/tenant dimensions. tokenType is "ivs" here - a boolean isAwsHls could never
      // have told native that.
      ...playbackObservabilityPayload(this),
      // LAST, and unconditional. Live always has a signed IVS manifest and therefore always has
      // a deadline, so a finite expiresAtMs is structural here rather than incidental: it is
      // spread over, never under, and it never narrows to undefined or null.
      //
      // Android has since hardened LivePlayerComponent.ConnectData to `val expiresAtMs: Long? =
      // null` with a tokenHasExpired helper, so on current builds an absent key falls through to
      // the same branch a valid-but-expired token takes. Builds already in the field decode it
      // as non-nullable with no default, where an absent OR null key fails the whole decode and
      // handleConnectEvent bails before startLivePlayback - nothing plays at all, not degraded.
      // Android asked to keep both halves deliberately: their nullable decoder covers the builds
      // already shipped, this guarantee covers every build that ships after it.
      //
      // The general rule behind that asymmetry: adding a key is always safe (their bridge and
      // JSON decoders both ignore unknown keys), making an existing key conditional is not.
      //
      // This is the ONE surface where the key is mandatory. Everywhere else an absent expiry
      // correctly means "no advertised deadline", which is a different answer from "expired" -
      // do not "consistency-fix" the others into always sending one, and do not make this one
      // optional to match them.
      //
      // IVS validates the token only at playback initiation, so a running session outlives this
      // moment - native uses it purely to decide whether re-initiating a failed session needs a
      // page reload (fresh token) first.
      expiresAtMs: this.expiresAtMs(),
    });
  }

  // Always a finite number. Prefers the server-rendered live attribute (which
  // Media::VideoPlayerComponent#live_hls_expires_at_ms always emits for live), falls back to the
  // observability value, and finally to 0 - "unknown", but still decodable by Android.
  expiresAtMs() {
    const rendered = Number(this.element.getAttribute("data-hls-url-expires-at-ms"));
    if (Number.isFinite(rendered) && rendered > 0) return rendered;

    const observed = this.hasExpiresAtMsValue ? this.expiresAtMsValue : 0;
    return Number.isFinite(observed) ? observed : 0;
  }

  disconnect() {
    // When the stream ends, the page's turbo_stream_from subscription re-renders it as
    // not-live, so this disconnect doubles as native's stream-ended teardown signal.
    this.send("disconnect");
    super.disconnect();
  }
}
